<script setup>
import { ref, computed, onMounted } from 'vue'
import { useLocaleStore } from '@/stores/localeStore'
import { useWindowsStore } from '@/stores/windowsStore'
import CVToolbar from './CVToolbar.vue'

const localeStore = useLocaleStore()
const windowsStore = useWindowsStore()
const zoomLevel = ref(1)
const isZoomed = ref(false)
const pdfPosition = ref({ x: 0, y: 0 })
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })

const cvPdfSrc = computed(() => {
  // Use PDF from public/img/icons/cv folder
  return '/img/icons/cv/CV - Sobhan Izadi.pdf'
})

const handleZoom = () => {
  if (isZoomed.value) {
    // Reset zoom
    zoomLevel.value = 1
    isZoomed.value = false
    pdfPosition.value = { x: 0, y: 0 }
  } else {
    // Zoom in
    zoomLevel.value = 2
    isZoomed.value = true
  }
}

const handleSave = async () => {
  try {
    // Fetch the PDF file
    const response = await fetch(cvPdfSrc.value)
    const blob = await response.blob()
    
    // Create a blob URL and download
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = 'CV - Sobhan Izadi.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Error downloading PDF:', error)
    // Fallback to direct link
    const link = document.createElement('a')
    link.href = cvPdfSrc.value
    link.download = 'CV - Sobhan Izadi.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

const handlePrint = () => {
  window.print()
}

const handleContact = () => {
  // Open contact window - dispatch custom event that Office.vue will listen to
  window.dispatchEvent(new CustomEvent('open-window', { detail: { windowId: 'contact' } }))
}

const startDrag = (e) => {
  if (!isZoomed.value) return
  isDragging.value = true
  dragStart.value = {
    x: e.clientX - pdfPosition.value.x,
    y: e.clientY - pdfPosition.value.y
  }
}

const onDrag = (e) => {
  if (!isDragging.value || !isZoomed.value) return
  pdfPosition.value = {
    x: e.clientX - dragStart.value.x,
    y: e.clientY - dragStart.value.y
  }
}

const stopDrag = () => {
  isDragging.value = false
}

onMounted(() => {
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
})
</script>

<template>
  <div class="relative right-0 overflow-hidden bg-white" style="height: 100%; display: flex; flex-direction: column;">
    <!-- Custom Toolbar -->
    <CVToolbar @zoom="handleZoom" @save="handleSave" @print="handlePrint" @contact="handleContact" />
    
    <!-- CV PDF Viewer -->
    <div 
      class="w-full bg-white overflow-hidden relative flex-1 border-window-header-bot"
      :style="{ margin: 0, padding: 0, minHeight: 0 }"
      @mousedown="startDrag"
    >
      <div 
        class="w-full h-full relative"
        :style="{
          transform: `scale(${zoomLevel}) translate(${pdfPosition.x / zoomLevel}px, ${pdfPosition.y / zoomLevel}px)`,
          transformOrigin: 'top left',
          transition: isDragging ? 'none' : 'transform 0.3s ease',
          cursor: isZoomed ? 'move' : 'default',
          margin: 0,
          padding: 0,
          width: '100%',
          height: '100%'
        }"
      >
        <iframe
          :src="cvPdfSrc"
          class="w-full h-full border-0"
          :style="{
            'pointer-events': isDragging ? 'none' : 'auto',
            margin: 0,
            padding: 0,
            display: 'block',
            width: '100%',
            height: '100%',
            border: 'none'
          }"
        ></iframe>
      </div>
      
      <!-- Zoom hint -->
      <div 
        v-if="!isZoomed"
        class="absolute bottom-2 left-2 text-xs text-gray-600 bg-white bg-opacity-75 px-2 py-1 rounded z-10 pointer-events-none"
      >
        Click zoom button to zoom in, then drag to view other areas
      </div>
    </div>
  </div>
</template>
